import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Car, Home, Layers, QrCode, UserCircle } from 'lucide-react';

import { Colors } from './theme';

/**
 * The supervisor's app, in a browser.
 *
 * The Android build is the app a supervisor is trained on; the iOS half of the
 * floor gets this page instead. So the shell is the phone's, not the web's — one
 * column at a handset's width, the five tabs at the bottom in the same order,
 * and the scanner keeping the middle seat. On a laptop the column is centred
 * rather than stretched, because the point of this screen is that what is
 * pointed at during a session looks the same on all three devices.
 *
 * The tab order matches `mobile-frontend/app/(tabs)/_layout.tsx` for a
 * supervisor: Home · Plates · Scan · Dispatch · Profile. Orders and History are
 * not his — he works from one list on Home, and what he has passed is a row on
 * Profile.
 */

const TABS = [
  { key: 'home', path: '/supervisor', labelKey: 'nav.home', icon: Home },
  { key: 'plates', path: '/supervisor/plates', labelKey: 'nav.plates', icon: Layers },
  { key: 'scan', path: '/supervisor/scan', labelKey: 'nav.scan', icon: QrCode, raised: true },
  { key: 'dispatch', path: '/supervisor/dispatch', labelKey: 'nav.dispatch', icon: Car },
  { key: 'profile', path: '/supervisor/profile', labelKey: 'nav.profile', icon: UserCircle },
];

/** Which tab a path belongs to — a job card opened from the board is still Home. */
function activeTab(pathname) {
  const match = TABS.filter((t) => t.path !== '/supervisor')
    .find((t) => pathname.startsWith(t.path));
  return match ? match.key : 'home';
}

export default function PhoneShell({ children, tabs = true }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const active = activeTab(pathname);

  return (
    <div className="min-h-[100dvh] w-full" style={{ backgroundColor: Colors.neutral[100] }}>
      <div
        className="relative mx-auto flex min-h-[100dvh] w-full max-w-[26rem] flex-col shadow-sm"
        style={{ backgroundColor: Colors.background.secondary }}
      >
        <div className={`flex flex-1 flex-col ${tabs ? 'pb-[76px]' : ''}`}>{children}</div>

        {tabs && (
          <nav
            aria-label="Supervisor"
            className="fixed bottom-0 left-1/2 z-40 flex h-[68px] w-full max-w-[26rem] -translate-x-1/2 items-start justify-around border-t pt-2.5"
            style={{ backgroundColor: Colors.background.primary, borderColor: Colors.border.default }}
          >
            {TABS.map((tab) => {
              const on = active === tab.key;
              const colour = on ? Colors.accent.DEFAULT : Colors.text.disabled;
              const Icon = tab.icon;
              return (
                <button
                  key={tab.key}
                  type="button"
                  data-testid={`tab-${tab.key}`}
                  aria-current={on ? 'page' : undefined}
                  onClick={() => navigate(tab.path)}
                  className="flex min-w-0 flex-1 flex-col items-center gap-1"
                >
                  {tab.raised ? (
                    <span
                      className="flex h-[52px] w-[52px] -translate-y-1 items-center justify-center rounded-full border-[1.5px]"
                      style={{
                        borderColor: colour,
                        backgroundColor: on ? Colors.accent.wash : Colors.background.secondary,
                      }}
                    >
                      <Icon className="h-[26px] w-[26px]" style={{ color: colour }} />
                    </span>
                  ) : (
                    <Icon className="h-6 w-6" style={{ color: colour }} />
                  )}
                  <span
                    className={`truncate text-[10px] font-medium tracking-[0.5px] ${tab.raised ? '-mt-1' : ''}`}
                    style={{ color: colour }}
                  >
                    {t(tab.labelKey)}
                  </span>
                </button>
              );
            })}
          </nav>
        )}
      </div>
    </div>
  );
}
